import { useId, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";

interface ShippedModalProps {
    isProcessing: boolean;
    onConfirm: (trackingNumber: string, carrier: string) => void;
    onClose: () => void;
}

const schema = z.object({
    trackingNumber: z.string().min(1, "Tracking number is required"),
    carrier: z.string().min(1, "Carrier is required"),
});

type FormValues = z.infer<typeof schema>;

export const ShippedModal = ({
    isProcessing,
    onConfirm,
    onClose,
}: ShippedModalProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
    });

    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing,
        onRequestClose: onClose,
    });

    const onSubmit = (values: FormValues) => {
        onConfirm(values.trackingNumber, values.carrier);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                {...panelProps}
                className="w-full max-w-md rounded-lg bg-base-100 p-6 shadow-xl focus:outline-none"
            >
                <h2 id={titleId} className="mb-4 text-lg font-bold">
                    Mark as Shipped
                </h2>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <label htmlFor="trackingNumber" className="label">
                            <span className="label-text">Tracking Number</span>
                        </label>
                        <input
                            id="trackingNumber"
                            {...register("trackingNumber")}
                            className="input input-bordered w-full"
                            placeholder="e.g. 1Z999AA10123456784"
                        />
                        {errors.trackingNumber && (
                            <p className="mt-1 text-sm text-error">
                                {errors.trackingNumber.message}
                            </p>
                        )}
                    </div>
                    <div>
                        <label htmlFor="carrier" className="label">
                            <span className="label-text">Carrier</span>
                        </label>
                        <input
                            id="carrier"
                            {...register("carrier")}
                            className="input input-bordered w-full"
                            placeholder="e.g. UPS, FedEx, USPS"
                        />
                        {errors.carrier && (
                            <p className="mt-1 text-sm text-error">
                                {errors.carrier.message}
                            </p>
                        )}
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            type="button"
                            color={ComponentColor.Ghost}
                            size={ComponentSize.SM}
                            onClick={onClose}
                            disabled={isProcessing}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color={ComponentColor.Accent}
                            size={ComponentSize.SM}
                            disabled={isProcessing}
                        >
                            {isProcessing ? "Saving…" : "Confirm Shipped"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
